import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Container, List, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { messagesService } from '../services/messagesService';
import { ConversationListItem } from '../types/messages.types';
import { usePolling } from '@/shared/hooks/usePolling';
import { AppButton } from '@/shared/components/buttons/AppButton';
import { MessagesHeader } from './MessagesHeader';
import { ConversationItem } from './ConversationItem';

const PAGE_LIMIT = 10;
const POLLING_INTERVAL = 15000; // 15 seconds
const INITIAL_DELAY = 15000; // 15 seconds offset from unread count polling

export function MessagesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadConversations = useCallback(
    async (nextPage: number, append: boolean, search?: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await messagesService.getConversations(
          nextPage,
          PAGE_LIMIT,
          search || undefined
        );
        setConversations(prev => (append ? [...prev, ...response.data] : response.data));
        setPage(response.meta.page);
        setHasMore(response.meta.page < response.meta.totalPages);
      } catch (err) {
        console.error('Failed to load conversations', err);
        setError(t('common.error'));
        throw err; // Re-throw to trigger polling backoff
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  // Polling callback with staggered delay
  const pollConversations = useCallback(async () => {
    if (!document.hidden) {
      await loadConversations(1, false, searchTerm);
    }
  }, [loadConversations, searchTerm]);

  const { start: startPolling, stop: stopPolling } = usePolling({
    callback: pollConversations,
    interval: POLLING_INTERVAL,
    pauseOnHidden: true,
    enableBackoff: true,
    maxRetries: 3,
  });

  // Initial load with offset
  useEffect(() => {
    const timer = setTimeout(() => {
      loadConversations(1, false, searchTerm);
    }, INITIAL_DELAY);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start polling after initial load
  useEffect(() => {
    startPolling();
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling, pollConversations]);

  // Handle search term changes
  useEffect(() => {
    loadConversations(1, false, searchTerm);
  }, [searchTerm, loadConversations]);

  // Scroll to top when page is accessed
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLoadMore = () => {
    if (!isLoading) {
      loadConversations(page + 1, true, searchTerm);
    }
  };

  const handleSearchChange = useCallback((search: string) => {
    setSearchTerm(search);
    setPage(1);
  }, []);

  const handleConversationClick = useCallback(
    async (conversationId: string) => {
      // Optimistically remove unread badge
      setConversations(prev =>
        prev.map(conv => (conv.id === conversationId ? { ...conv, hasUnread: false } : conv))
      );

      // Mark as read in backend
      try {
        await messagesService.markConversationRead(conversationId);
      } catch (error) {
        console.error('Failed to mark conversation as read:', error);
        // Revert optimistic update on error
        setConversations(prev =>
          prev.map(conv => (conv.id === conversationId ? { ...conv, hasUnread: true } : conv))
        );
      }

      navigate(`/messages/${conversationId}`);
    },
    [navigate]
  );

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', mt: 10 }}>
      <MessagesHeader onSearchChange={handleSearchChange} />
      <Container maxWidth="md" sx={{ px: 0, pb: 4 }}>
        {isLoading && conversations.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {!isLoading && error ? (
          <Box sx={{ px: 3, mt: 3 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : null}

        {!isLoading && !error && conversations.length === 0 ? (
          <Box sx={{ px: 3, mt: 4 }}>
            <Typography color="text.secondary" variant="body2">
              {searchTerm ? t('messages.emptySearch') : t('messages.empty')}
            </Typography>
          </Box>
        ) : null}

        <List disablePadding>
          {conversations.map((conversation, index) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              showDivider={index < conversations.length - 1}
              onClick={() => handleConversationClick(conversation.id)}
            />
          ))}
        </List>

        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <AppButton variant="outlined" onClick={handleLoadMore} disabled={isLoading}>
              {isLoading ? t('common.loading') : t('messages.loadMore')}
            </AppButton>
          </Box>
        )}
      </Container>
    </Box>
  );
}
